/**
 * Assign Role Modal
 * Modal for assigning roles to community members
 * Uses shadcn Dialog pattern
 */

import { User } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import type { CommunityMember } from "../services/community.service"
import { getCommunityRoles, updateMemberRoles } from "../services/community.service"

interface AssignRoleModalProps {
  isOpen: boolean
  onClose: () => void
  member: CommunityMember | null
  communityId: string
  isOwner: boolean // Whether current user is community owner
  onSuccess: () => void // Callback after successful role assignment
}

// Role options with descriptions
const ROLE_OPTIONS = [
  {
    value: "MEMBER",
    label: "Member",
    description: "Can join rituals and participate in discussions.",
  },
  {
    value: "RITUALIST",
    label: "Ritualist",
    description: "Leads rituals and shares guidance with members.",
  },
  {
    value: "MODERATOR",
    label: "Moderator",
    description: "Helps manage discussions and community activity.",
  },
  {
    value: "OWNER",
    label: "Owner",
    description: "Full control over the community, members, and rituals.",
  },
]

interface Role {
  id: string
  title: string
}

export function AssignRoleModal({
  isOpen,
  onClose,
  member,
  communityId,
  isOwner,
  onSuccess,
}: AssignRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [roles, setRoles] = useState<Role[]>([])

  // Fetch roles when modal opens
  useEffect(() => {
    if (isOpen) {
      getCommunityRoles()
        .then(fetchedRoles => {
          setRoles(fetchedRoles)
        })
        .catch(() => {
          toast.error("Failed to fetch roles. Please try again.")
        })
    }
  }, [isOpen])

  // Filter role options based on whether user is owner
  const availableRoles = isOwner
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter(role => role.value !== "OWNER")

  // Get member's current roles
  const currentRoles = member?.roleTitles || []

  const handleSave = async () => {
    if (!member || !selectedRole || roles.length === 0) return

    setIsLoading(true)

    try {
      // Check if member already has this role
      const roleLabel = ROLE_OPTIONS.find(r => r.value === selectedRole)?.label || selectedRole
      if (currentRoles.some(r => r.toUpperCase() === selectedRole)) {
        toast.error(`User is already ${roleLabel}`)
        setIsLoading(false)
        return
      }

      // Find the role ID for the selected role title
      const role = roles.find(r => r.title === selectedRole)
      if (!role) {
        toast.error("Role not found. Please try again.")
        setIsLoading(false)
        return
      }

      // Call the API to update the member's role
      await updateMemberRoles(communityId, [
        {
          userId: member.id,
          roleId: role.id,
        },
      ])

      toast.success(`Role assigned successfully`)
      onSuccess()
      onClose()
      setSelectedRole("")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update role")
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedRole("")
    onClose()
  }

  if (!member) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && handleClose()}>
      <DialogContent
        className="md:w-[530px] w-[95vw] max-h-[484px] bg-white/15 rounded-3xl py-4 px-5"
        overlayClass="bg-[rgb(0,0,0,0.9)]"
        onPointerDownOutside={e => e.preventDefault()}
      >
        {/* Header with Close Button */}
        <div className="mt-0">
          <DialogTitle className="text-charcoal text-[18px] mb-1 font-monteserat font-semibold">
            Assign role
          </DialogTitle>
          <DialogDescription className="">
            Choose a role for this member in the community.
          </DialogDescription>
        </div>

        {/* Member Info */}
        <div className="flex items-center gap-3 mt-1 bg-muted/30 rounded-lg">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-themed">
            {member.coverImage ? (
              <img
                src={member.coverImage}
                alt={member.name || "User"}
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <User className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex gap-3 items-center">
            <p className="text-[14px] leading-[20px] font-semibold font-monteserat text-charcoal">
              {member.name || "No name"}
            </p>
            <p className="text-[12px] font-monteserat leading-[20px] text-body">
              {member.username ? `@${member.username}` : ""}
            </p>
          </div>
        </div>

        {/* Role Options */}
        <div className="space-y-2.5 mt-2.5">
          {availableRoles.map(role => (
            <label
              key={role.value}
              className={`flex items-start gap-3 cursor-pointer transition-colors`}
            >
              <input
                type="radio"
                name="role"
                value={role.value}
                checked={selectedRole === role.value}
                onChange={() => setSelectedRole(role.value)}
                className="mt-1 h-6 w-6 border-brand-olive bg-transparent cursor-pointer appearance-none rounded-full border-2 border-solid checked:bg-brand-olive checked:border-brand-olive"
              />
              <div className="flex-1">
                <span className="text-[14px] leading-[15px] font-medium text-charcoal">
                  {role.label}
                </span>
                <p className="text-[13px] text-body mt-1">{role.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-2 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="px-12 py-5 uppercase"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="px-15 py-5 uppercase"
            onClick={handleSave}
            disabled={!selectedRole || isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
